import { useEffect, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate, useParams } from 'react-router-dom'
import { db, kvGet, payOrder, type PayMethod } from '../lib/db'
import { defaultPrinter, printReceipt, probeAgent, type PrinterConfig } from '../lib/agent'
import { cashSuggestions, money, qtyToText } from '../lib/money'
import { useApp } from '../lib/store'
import { pushNow } from '../lib/sync'
import { AppBar } from '../components/AppBar'
import { NumPad } from '../components/NumPad'
import { VietQr } from '../components/VietQr'

export function PayScreen() {
  const { orderId = '' } = useParams()
  const nav = useNavigate()
  const say = useApp((s) => s.say)
  const shop = useApp((s) => s.shop)

  const [method, setMethod] = useState<PayMethod>('Cash')
  const [given, setGiven] = useState(0)
  const [done, setDone] = useState<{ total: number; change: number } | null>(null)
  const [printer, setPrinter] = useState<PrinterConfig | null>(null)
  const [printing, setPrinting] = useState(false)

  // Hỏi trước xem máy này có in được không, để nút In bill chỉ hiện khi bấm vào là có
  // giấy chạy ra. Nút bấm không làm gì cả còn tệ hơn là không có nút.
  useEffect(() => {
    void (async () => {
      const saved = await kvGet<PrinterConfig>('printer')
      if (!saved?.address) return
      if (await probeAgent()) setPrinter(saved ?? defaultPrinter)
    })()
  }, [])

  const order = useLiveQuery(() => db.orders.get(orderId), [orderId])
  if (!order) return <div className="screen" />

  const total = order.total
  const change = given - total
  const canPay = method === 'Transfer' || given >= total

  async function pay() {
    if (!order || !canPay) return
    await payOrder(order.id, method, method === 'Cash' ? given : total)
    setDone({ total: order.total, change: method === 'Cash' ? Math.max(0, given - order.total) : 0 })
    void pushNow()
  }

  if (done) {
    return (
      <div className="screen">
        <div className="body center">
          <div className="done">
            <div className="done-mark" aria-hidden="true">
              ✓
            </div>
            <h2>Đã thu {money(done.total)}đ</h2>
            {done.change > 0 ? (
              <p className="done-change">
                Thối lại khách <b className="num">{money(done.change)}đ</b>
              </p>
            ) : (
              <p>{method === 'Cash' ? 'Khách đưa đúng tiền' : 'Đã nhận chuyển khoản'}</p>
            )}
          </div>
        </div>
        <div className="dock">
          {printer && (
            <button
              type="button"
              className="btn btn-ghost"
              disabled={printing}
              onClick={async () => {
                setPrinting(true)
                try {
                  const fresh = await db.orders.get(orderId)
                  if (fresh) await printReceipt(fresh, shop, printer)
                } catch (e) {
                  say(e instanceof Error ? e.message : 'Không in được')
                }
                setPrinting(false)
              }}
            >
              {printing ? 'Đang in…' : 'In bill'}
            </button>
          )}
          <button type="button" className="btn btn-brand grow" onClick={() => nav('/tables')}>
            Xong
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="screen">
      <AppBar
        title="Thu tiền"
        sub={`${order.tableName ?? 'Bán nhanh'} · ${qtyToText(order.items.reduce((s, i) => s + i.qtyMilli, 0))} món`}
        onBack={() => nav(`/order/${order.id}`)}
      />

      <div className="body">
        <div className="pay-total">
          <div className="pay-row big">
            <span>Khách trả</span>
            <b className="num">{money(total)}</b>
          </div>

          {method === 'Cash' && (
            <>
              <div className="pay-row">
                <span>Khách đưa</span>
                <b className="num">{given ? money(given) : '—'}</b>
              </div>
              <div className={`pay-row change${change < 0 ? ' short' : ''}`}>
                <span>{change < 0 ? 'Còn thiếu' : 'Tiền thối'}</span>
                <b className="num">{given ? money(Math.abs(change)) : '—'}</b>
              </div>
            </>
          )}
        </div>

        <div className="seg seg-lg">
          <button type="button" aria-pressed={method === 'Cash'} onClick={() => setMethod('Cash')}>
            💵 Tiền mặt
          </button>
          <button type="button" aria-pressed={method === 'Transfer'} onClick={() => setMethod('Transfer')}>
            📱 Chuyển khoản
          </button>
        </div>

        {method === 'Cash' ? (
          <>
            <div className="chip-row chip-cash">
              {cashSuggestions(total).map((v) => (
                <button
                  key={v}
                  type="button"
                  className="chip"
                  aria-pressed={given === v}
                  onClick={() => setGiven(v)}
                >
                  {v === total ? 'Đúng tiền' : money(v)}
                </button>
              ))}
            </div>
            <NumPad value={given} onChange={setGiven} />
          </>
        ) : (
          <VietQr shop={shop} amount={total} code={order.code} />
        )}
      </div>

      <div className="dock">
        <button type="button" className="btn btn-brand btn-lg grow" disabled={!canPay} onClick={pay}>
          {canPay ? `Xong · ${money(total)}đ` : `Còn thiếu ${money(Math.abs(change))}đ`}
        </button>
      </div>
    </div>
  )
}
